import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { CheckCircle2, Send, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

const ASSISTANCE_OPTIONS = ["Interview / professional clothing", "Children's clothing", "Work uniforms / shoes", "Winter / seasonal clothing"];
const INCOME_OPTIONS = ["Under $15,000", "$15,000 - $25,000", "$25,001 - $40,000", "$40,001 - $60,000", "Over $60,000", "Prefer not to say"];
const EMPLOYMENT_OPTIONS = ["Unemployed - seeking work", "Employed part-time", "Employed full-time", "Student", "Unable to work / disability", "Retired"];
const ASSISTANCE_PROGRAMS = ["SNAP (Food Stamps)", "TANF", "Medicaid", "SSI / SSDI", "Housing Assistance (Section 8)", "Unemployment Benefits", "WIC", "None"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIMES = ["Morning (8am-12pm)", "Afternoon (12pm-4pm)", "Evening (4pm-8pm)"];

const emptyForm = {
  first_name: "", last_name: "", email: "", phone: "", city: "", state: "CO", zip_code: "",
  date_of_birth: "", assistance_type: [], household_size: "", household_income: "",
  employment_status: "", employment_details: "", government_assistance: [],
  government_assistance_details: "", contact_days: [], contact_times: [], notes: "",
};

const SubmitReferral = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.household_size || !form.household_income || !form.employment_status) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/referrals`, { ...form, household_size: parseInt(form.household_size, 10) });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="referral-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-9 h-9 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Referral Submitted</h1>
        <p className="text-slate-600 mb-6">The referral has been recorded and our team has been notified. You can view it under "My Referrals".</p>
        <Button onClick={() => { setForm(emptyForm); setDone(false); }} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="new-referral-btn">
          <UserPlus className="w-4 h-4" /> Submit Another Referral
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="submit-referral-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Submit a Referral</h1>
        <p className="text-slate-500 mt-1">Refer a client for clothing assistance through Soar Your Visibility</p>
      </div>
      <form onSubmit={submit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Client Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>First Name *</Label><Input className="mt-1" data-testid="ref-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></div>
            <div><Label>Last Name *</Label><Input className="mt-1" data-testid="ref-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
            <div><Label>Date of Birth</Label><Input type="date" className="mt-1" data-testid="ref-dob" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></div>
            <div><Label>Phone *</Label><Input className="mt-1" data-testid="ref-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><Label>Email *</Label><Input type="email" className="mt-1" data-testid="ref-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label>City</Label><Input className="mt-1" data-testid="ref-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>State</Label><Input className="mt-1" data-testid="ref-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
              <div><Label>ZIP</Label><Input className="mt-1" data-testid="ref-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Assistance Needed</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ASSISTANCE_OPTIONS.map((o) => (
              <label key={o} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`ref-assistance-${o}`}>
                <Checkbox checked={form.assistance_type.includes(o)} onCheckedChange={() => toggle("assistance_type", o)} />
                <span className="text-sm text-slate-700">{o}</span>
              </label>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Eligibility Screening</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Household Size *</Label><Input type="number" min="1" className="mt-1" data-testid="ref-household-size" value={form.household_size} onChange={(e) => set("household_size", e.target.value)} /></div>
              <div>
                <Label>Annual Household Income *</Label>
                <Select value={form.household_income} onValueChange={(v) => set("household_income", v)}>
                  <SelectTrigger className="mt-1" data-testid="ref-income"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>{INCOME_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Employment Status *</Label>
              <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                <SelectTrigger className="mt-1" data-testid="ref-employment"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>{EMPLOYMENT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Government Assistance Received</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {ASSISTANCE_PROGRAMS.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox checked={form.government_assistance.includes(p)} onCheckedChange={() => toggle("government_assistance", p)} />{p}
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Contact Availability</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label>Available Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAYS.map((d) => (
                  <button type="button" key={d} onClick={() => toggle("contact_days", d)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.contact_days.includes(d) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{d}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Available Times</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {TIMES.map((t) => (
                  <button type="button" key={t} onClick={() => toggle("contact_times", t)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.contact_times.includes(t) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{t}</button>
                ))}
              </div>
            </div>
            <div><Label>Notes</Label><Textarea className="mt-1" rows={3} data-testid="ref-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white gap-2 px-8" data-testid="submit-referral-btn">
            {loading ? "Submitting..." : <><Send className="w-4 h-4" /> Submit Referral</>}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SubmitReferral;
